import cn from "classnames";

import { getBorderRadiusProps } from "@/components/BorderRadius/BorderRadius";
import { getFocusOutlineProps } from "@/components/FocusOutline/FocusOutline";
import { mergeProps } from "@/utils/reactExtensions";

import styles from "./IconButton.module.scss";
import { IconButtonProps } from "./IconButton.types";

/**
 * IconButton component
 *
 * This component renders a button with an icon. It supports various styles and states.
 *
 * - Note: The "bare" variant will have auto height which can be controlled by flex properties to stretch the height on the consumer side.
 *
 * Accessibility:
 * - One of the `aria-*` attributes is required for accessibility. This could be `aria-label`, `aria-describedby`, etc.
 *   - Note: Ensure to provide appropriate `aria-*` attributes to make the button accessible.
 *
 * Usage Guidelines:
 * - It is recommended to use SVG components as icons for better scalability and performance.
 * - The "default" variant will change color based on the theme. Ensure to test the button in different themes to avoid unexpected behavior.
 *
 * Usage:
 *
 * ```tsx
 * <IconButton
 *   icon={<SVGChevronLeft />}
 *   isShadowed={true}
 *   disabled={false}
 *   variant="bare"
 *   onClick={handleClick}
 * />
 * ```
 */
export const IconButton = /*#__PURE__*/ (function () {
  const IconButton: React.FC<IconButtonProps> = ({
    icon,
    isShadowed = false,
    disabled,
    buttonRef,
    variant = "default",
    size = "default",
    isRounded = false,
    ...rest
  }) => {
    const isBare = variant === "bare";

    return (
      <button
        aria-disabled={disabled}
        disabled={disabled}
        ref={buttonRef}
        {...mergeProps(
          rest,
          getFocusOutlineProps(),
          getBorderRadiusProps(isBare && !isRounded ? "small" : "rounded"),
          {
            className: cn(styles.iconButton, {
              [styles.shadowed]: isShadowed && variant === "default",
              [styles.disabled]: disabled,
              [styles.bare]: isBare,
              [styles.subtle]: variant === "subtle",
              [styles.highlight]: variant === "highlight",
              [styles.small]: size === "small",
            }),
          }
        )}
      >
        {icon}
      </button>
    );
  };

  IconButton.displayName = "IconButton";

  return IconButton;
})();
